import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Button } from '../../UI/Buttons';
import { Title } from '../../UI/Typography';
import { FormGroup, Input } from '../../UI/Inputs';
import { Label } from '../../UI/Typography';
import { updateUser } from '../../../Services/Api/userService';

const FormContainer = styled.div`
  font-family: ${({ theme }) => theme.typography.fontFamily};
  padding: ${({ theme }) => theme.spacing.md};
`;

const SubmitButton = styled(Button)`
  margin-top: ${({ theme }) => theme.spacing.md};
`;

const StatusMessage = styled.div`
  color: ${props => (props.type === 'success' ? props.theme.colors.success : props.theme.colors.error)};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
`;

const RoleTag = styled.span`
  display: inline-block;
  background: ${({ theme }) => theme.colors.primary}22;
  color: ${({ theme }) => theme.colors.primary};
  border-radius: 12px;
  padding: 2px 10px;
  margin: 0 6px 6px 0;
  font-size: 0.95em;
  font-weight: 500;
`;

const RemoveRoleButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.error};
  margin-left: 4px;
  cursor: pointer;
  font-size: 1em;
`;

function UserEditForm({ userToEdit, onSuccessfulUpdate, onClose }) {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState([]); // Array of roles
  const [roleInput, setRoleInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  useEffect(() => {
    if (userToEdit) {
      setUsername(userToEdit.username || '');
      setEmail(userToEdit.email || '');
      setFirstName(userToEdit.first_name || '');
      setLastName(userToEdit.last_name || '');
      setRole(Array.isArray(userToEdit.role) ? userToEdit.role : (userToEdit.role ? [userToEdit.role] : []));
      setRoleInput('');
      setSubmitStatus(null);
    }
  }, [userToEdit]);

  const handleRoleInputChange = (e) => {
    setRoleInput(e.target.value);
  };

  const handleRoleInputKeyDown = (e) => {
    if (e.key === 'Enter' && roleInput.trim()) {
      e.preventDefault();
      if (!role.includes(roleInput.trim())) {
        setRole([...role, roleInput.trim()]);
      }
      setRoleInput('');
    }
  };

  const handleRemoveRole = (removeRole) => {
    setRole(role.filter(r => r !== removeRole));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!userToEdit) return;

    setIsLoading(true);
    setSubmitStatus(null);

    const updatedData = {
      username: username,
      email: email,
      first_name: firstName,
      last_name: lastName,
      role: role, // Always an array
    };

    try {
      await updateUser(userToEdit.id, updatedData);

      setIsLoading(false);
      setSubmitStatus({ type: 'success', message: 'User updated successfully!' });

      if (onSuccessfulUpdate) {
        setTimeout(() => {
          onSuccessfulUpdate();
        }, 1500);
      }
    } catch (error) {
      setIsLoading(false);
      setSubmitStatus({ type: 'error', message: error.message || 'Failed to update user.' });
    }
  };

  if (!userToEdit) {
    return <p>No user selected for editing.</p>;
  }

  return (
    <FormContainer>
      <Title>Edit User</Title>
      <form onSubmit={handleSubmit}>
        <FormGroup>
          <Label htmlFor="editUserUsername">Username:</Label>
          <Input
            type="text"
            id="editUserUsername"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
            disabled={isLoading}
          />
        </FormGroup>
        <FormGroup>
          <Label htmlFor="editUserEmail">Email:</Label>
          <Input
            type="email"
            id="editUserEmail"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={isLoading}
          />
        </FormGroup>

        <FormGroup>
          <Label htmlFor="editUserFirstName">First Name:</Label>
          <Input
            type="text"
            id="editUserFirstName"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            required
            disabled={isLoading}
          />
        </FormGroup>

        <FormGroup>
          <Label htmlFor="editUserLastName">Last Name:</Label>
          <Input
            type="text"
            id="editUserLastName"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            required
            disabled={isLoading}
          />
        </FormGroup>

        <FormGroup>
          <Label htmlFor="editUserRole">Role(s):</Label>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 4 }}>
            {role.map((r, idx) => (
              <RoleTag key={idx}>
                {r}
                <RemoveRoleButton type="button" onClick={() => handleRemoveRole(r)} title="Remove role">
                  ×
                </RemoveRoleButton>
              </RoleTag>
            ))}
            <Input
              type="text"
              id="editUserRole"
              value={roleInput}
              onChange={handleRoleInputChange}
              onKeyDown={handleRoleInputKeyDown}
              disabled={isLoading}
              placeholder="Type role and press Enter"
              style={{ minWidth: 120, flex: 1 }}
            />
          </div>
        </FormGroup>

        {submitStatus && (
          <StatusMessage type={submitStatus.type}>
            {submitStatus.message}
          </StatusMessage>
        )}

        <SubmitButton type="submit" disabled={isLoading}>
          {isLoading ? 'Updating...' : 'Update User'}
        </SubmitButton>
      </form>
    </FormContainer>
  );
}

export default UserEditForm;
